// @flow
import classnames from 'classnames'
import React from 'react'

/**
 * MultiWayToggle<Value> - a toggle component with multiple options
 *
 * @param {Object} props - the react props
 * @param {Value} props.selectedOption - the currently selected option
 * @param {Array<{ value: Value, label: string | React$Node, ariaLabel: string }>} props.options - the available options to select from
 * @param {(option: Value) => void} props.onSelectOption - a callback for when a user select a new option
 * @param {string} props.className? - the class name to apply to the options wrapper
 *
 * @return {React$Node}
 */
export function MultiWayToggle<Value>({
	options,
	selectedOption,
	onSelectOption,
	className,
}: {
	options: Array<{ value: Value, label: string | React$Node, ariaLabel: string }>,
	selectedOption: Value,
	onSelectOption: (option: Value) => void,
	className?: string,
}): React$Node {
	return (
		<div
			className={classnames('relative flex rounded-lg overflow-hidden w-min gap-0.5', className)}>
			{options.map((option, index) => (
				<button
					key={index}
					onClick={() => {
						if (selectedOption === option.value) {
							return
						}
						onSelectOption(option.value)
					}}
					className={classnames(
						'min-w-8 flex justify-center items-center px-2 flex-grow',
						option.value === selectedOption ? ' bg-game-primary-green' : 'bg-primary-500'
					)}
					type="button"
					aria-label={option.ariaLabel}
					aria-pressed={option.value === selectedOption}>
					<span className="pointer-events-none">{option.label}</span>
				</button>
			))}
		</div>
	)
}
